import { forwardRef, useCallback, useMemo } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { useDataTableContext } from './hooks/index.js';
import { formatValue, getColumnGroups, getColumnId, getValue } from './utils/index.js';
import { DataTableCell, DataTableCellProps } from './DataTableCell.js';
import { DataTableColumn } from './DataTableColumn.js';
import { DataTableColumnGroup } from './DataTableColumnGroup.js';
import { DataTableColumnDefinition, DataTableRow } from './DataTableTypes.js';

export type DataTableBodyElement = HTMLDivElement;

export type DataTableBodyProps<TRow extends DataTableRow = DataTableRow, TContext = unknown> = Omit<
  React.HTMLAttributes<DataTableBodyElement>,
  'children'
> & {
  dense?: boolean;
  onRowClick?: (evt: React.MouseEvent, row: TRow) => void;
  columnKey?: (params: {
    column: DataTableColumnDefinition<TRow, TContext>;
    columnIndex: number;
  }) => string;
  rowKey?: (params: {
    row: TRow;
    column: DataTableColumnDefinition<TRow, TContext>;
    columnIndex: number;
    rowIndex: number;
  }) => string;
  cellComponent?: React.ComponentType<DataTableCellProps<TRow, TContext>>;
};

export const DataTableBody = styled(
  forwardRef(
    <TRow extends DataTableRow = DataTableRow, TContext = unknown>(
      {
        onRowClick,
        dense,
        columnKey = ({ column }) => `data-table-body-column-${getColumnId(column)}`,
        rowKey = ({ column, rowIndex }) =>
          `data-table-body-cell-${getColumnId(column)}:${rowIndex}`,
        cellComponent: CellComponent = DataTableCell as React.ComponentType<
          DataTableCellProps<TRow, TContext>
        >,
        ...restProps
      }: DataTableBodyProps<TRow, TContext>,
      ref: React.ForwardedRef<DataTableBodyElement>
    ) => {
      const { columns, rows, context } = useDataTableContext<TRow, TContext>();

      const handleCellClick = useCallback<
        PickProp<DataTableCellProps<TRow, TContext>, 'onCellClick'>
      >(
        (evt, { row }) => {
          if (row && onRowClick) {
            onRowClick(evt, row);
          }
        },
        [onRowClick]
      );

      const [pinnedLeftCols, flexCols, pinnedRightCols] = useMemo(() => {
        let colIdx = 0;
        return getColumnGroups(columns).map((columns) =>
          columns.map((column) => {
            const columnIndex = colIdx++;

            return (
              <DataTableColumn key={columnKey({ column, columnIndex })} column={column as any}>
                {rows.map((row, rowIndex) => {
                  const value = getValue(column, row);
                  const formattedValue: string = formatValue(column, row, value);
                  const children = column.renderCell ? (
                    column.renderCell({
                      row,
                      column,
                      value,
                      formattedValue,
                      context,
                    })
                  ) : (
                    <>{formattedValue}</>
                  );

                  return (
                    <CellComponent
                      children={children}
                      key={rowKey({ column, columnIndex, row, rowIndex })}
                      column={column}
                      columnIndex={columnIndex}
                      context={context}
                      dense={dense}
                      row={row}
                      rowIndex={rowIndex + 1}
                      tabIndex={0}
                      onCellClick={handleCellClick}
                    />
                  );
                })}
              </DataTableColumn>
            );
          })
        );
      }, [columns, columnKey, rows, CellComponent, rowKey, handleCellClick, dense, context]);

      return (
        <div ref={ref} {...restProps}>
          {!!pinnedLeftCols.length && (
            <DataTableColumnGroup pinned="left">{pinnedLeftCols}</DataTableColumnGroup>
          )}
          <DataTableColumnGroup>{flexCols}</DataTableColumnGroup>
          {!!pinnedRightCols.length && (
            <DataTableColumnGroup pinned="right">{pinnedRightCols}</DataTableColumnGroup>
          )}
        </div>
      );
    }
  )
).withConfig({
  shouldForwardProp: (prop) => !['dense'].includes(prop),
  displayName: 'DataTableBody',
})`
  display: flex;
  flex-direction: row;
  & > div:first-of-type > div:first-of-type > div {
    padding-left: ${({ dense }) => (dense ? '8px' : '16px')};
  }
  & > div:last-of-type > div:last-of-type > div {
    padding-right: ${({ dense }) => (dense ? '8px' : '16px')};
  }
`;
